import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { AxiosInstance } from 'axios';
import {
  apiHnsn,
  apiLaureano,
  apiLaureanoSoul,
  apiPhato,
} from '../services/api';

const AUTH_SCHEMA_KEY = '@cedapp:authSchemaVersion';
const AUTH_SCHEMA_VERSION = '2';

const AUTH_STORAGE_KEYS = [
  '@cedapp:tokenPhato',
  '@cedapp:userPhato',
  '@cedapp:phatoAccess',
  '@cedapp:tokenLaureano',
  '@cedapp:userLaureano',
  '@cedapp:tokenLaureanoSoul',
  '@cedapp:userLaureanoSoul',
  '@cedapp:tokenHnsn',
  '@cedapp:userHnsn',
  '@cedapp:isBusiness',
  // Chaves da versão que armazenava dados Patho como se fossem Laureano.
  '@cedapp:laureanoAccess',
  '@cedapp:laureanoAuthenticated',
];

export interface PhatoAccessContext {
  isMedico: boolean;
  isBusiness?: boolean;
  crm: string;
  cpf: string;
  cnpj?: string;
  dataNascimento: string;
}

export interface Usuario {
  id: number;
  nome: string;
  crm?: string;
  cpf?: string;
  dtNascimento: string;
  cdPrestadorArray?: string;
  CNPJ?: string;
  ALIAS?: string;
  NAME?: string;
}

export interface SignInCredentials {
  login: string;
  password: string;
  isMedico: boolean | string;
  isBusiness?: boolean | string;
}

interface AuthState {
  tokenPhato: string | null;
  tokenLaureano: string | null;
  tokenLaureanoSoul: string | null;
  tokenHnsn: string | null;
  usuarioPhato: Usuario | null;
  usuarioLaureano: Usuario | null;
  usuarioLaureanoSoul: Usuario | null;
  usuarioHnsn: Usuario | null;
  phatoAccess: PhatoAccessContext | null;
}

export type SessionApiName = 'phato' | 'laureano' | 'laureanoSoul' | 'hnsn';

export interface PendingSession {
  apiName: SessionApiName;
  token: string;
  user: Usuario;
}

interface AuthContextData extends AuthState {
  signInPhato(credentials: SignInCredentials): Promise<Usuario>;
  signInLaureano(credentials: SignInCredentials): Promise<Usuario>;
  signInLaureanoSoul(credentials: SignInCredentials): Promise<Usuario>;
  signInHnsn(credentials: SignInCredentials): Promise<Usuario>;
  requestSession(
    apiName: SessionApiName,
    credentials: SignInCredentials,
  ): Promise<PendingSession>;
  commitSessions(
    sessions: PendingSession[],
    phatoAccess: PhatoAccessContext | null,
  ): Promise<void>;
  setPhatoAccess(access: PhatoAccessContext): Promise<void>;
  clearSession(): void;
  signOut(): void;
  setData(state: AuthState): Promise<void>;
  loadStorageData(): Promise<void>;
}

type ApiSessionConfig = {
  api: AxiosInstance;
  tokenKey: string;
  userKey: string;
  tokenField: keyof Pick<
    AuthState,
    'tokenPhato' | 'tokenLaureano' | 'tokenLaureanoSoul' | 'tokenHnsn'
  >;
  userField: keyof Pick<
    AuthState,
    'usuarioPhato' | 'usuarioLaureano' | 'usuarioLaureanoSoul' | 'usuarioHnsn'
  >;
};

const emptyAuthState: AuthState = {
  tokenPhato: null,
  tokenLaureano: null,
  tokenLaureanoSoul: null,
  tokenHnsn: null,
  usuarioPhato: null,
  usuarioLaureano: null,
  usuarioLaureanoSoul: null,
  usuarioHnsn: null,
  phatoAccess: null,
};

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

function readStoredUser(key: string): Usuario | null {
  const serializedUser = sessionStorage.getItem(key);
  if (!serializedUser) return null;

  try {
    return JSON.parse(serializedUser) as Usuario;
  } catch (error) {
    sessionStorage.removeItem(key);
    return null;
  }
}

function clearStoredAuth() {
  AUTH_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
  sessionStorage.removeItem(AUTH_SCHEMA_KEY);
  [apiPhato, apiLaureano, apiLaureanoSoul, apiHnsn].forEach((api) => {
    delete api.defaults.headers.common['Authorization'];
  });
}

function buildSessionPayload(credentials: SignInCredentials) {
  const isMedico =
    credentials.isMedico === true || credentials.isMedico === 'true';
  const payload: Record<string, string | boolean> = {
    login: credentials.login,
    password: isMedico
      ? String(credentials.password).replace(/\D/g, '')
      : credentials.password,
    isMedico: credentials.isMedico,
  };

  if (credentials.isBusiness !== undefined) {
    payload.isBusiness = credentials.isBusiness;
  }

  return payload;
}

const sessionConfigs: Record<SessionApiName, ApiSessionConfig> = {
  phato: {
    api: apiPhato,
    tokenKey: '@cedapp:tokenPhato',
    userKey: '@cedapp:userPhato',
    tokenField: 'tokenPhato',
    userField: 'usuarioPhato',
  },
  laureano: {
    api: apiLaureano,
    tokenKey: '@cedapp:tokenLaureano',
    userKey: '@cedapp:userLaureano',
    tokenField: 'tokenLaureano',
    userField: 'usuarioLaureano',
  },
  laureanoSoul: {
    api: apiLaureanoSoul,
    tokenKey: '@cedapp:tokenLaureanoSoul',
    userKey: '@cedapp:userLaureanoSoul',
    tokenField: 'tokenLaureanoSoul',
    userField: 'usuarioLaureanoSoul',
  },
  hnsn: {
    api: apiHnsn,
    tokenKey: '@cedapp:tokenHnsn',
    userKey: '@cedapp:userHnsn',
    tokenField: 'tokenHnsn',
    userField: 'usuarioHnsn',
  },
};

async function requestApiSession(
  apiName: SessionApiName,
  credentials: SignInCredentials,
): Promise<PendingSession> {
  const response = await sessionConfigs[apiName].api.post(
    '/session',
    buildSessionPayload(credentials),
  );
  const { token, user } = response.data as { token: string; user: Usuario };

  return { apiName, token, user };
}

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>(emptyAuthState);

  const loadStorageData = useCallback(async (): Promise<void> => {
    const hasStoredAuthentication = AUTH_STORAGE_KEYS.some((key) =>
      sessionStorage.getItem(key),
    );

    if (
      hasStoredAuthentication &&
      sessionStorage.getItem(AUTH_SCHEMA_KEY) !== AUTH_SCHEMA_VERSION
    ) {
      clearStoredAuth();
      setData(emptyAuthState);
      return;
    }

    const tokenPhato = sessionStorage.getItem('@cedapp:tokenPhato');
    const tokenLaureano = sessionStorage.getItem('@cedapp:tokenLaureano');
    const tokenLaureanoSoul = sessionStorage.getItem(
      '@cedapp:tokenLaureanoSoul',
    );
    const tokenHnsn = sessionStorage.getItem('@cedapp:tokenHnsn');
    const serializedPhatoAccess = sessionStorage.getItem('@cedapp:phatoAccess');

    [
      [apiPhato, tokenPhato],
      [apiLaureano, tokenLaureano],
      [apiLaureanoSoul, tokenLaureanoSoul],
      [apiHnsn, tokenHnsn],
    ].forEach(([api, token]) => {
      const instance = api as AxiosInstance;
      if (token) {
        instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } else {
        delete instance.defaults.headers.common['Authorization'];
      }
    });

    let phatoAccess: PhatoAccessContext | null = null;
    if (serializedPhatoAccess) {
      try {
        phatoAccess = JSON.parse(serializedPhatoAccess);
      } catch (error) {
        sessionStorage.removeItem('@cedapp:phatoAccess');
      }
    }

    setData({
      tokenPhato,
      tokenLaureano,
      tokenLaureanoSoul,
      tokenHnsn,
      usuarioPhato: readStoredUser('@cedapp:userPhato'),
      usuarioLaureano: readStoredUser('@cedapp:userLaureano'),
      usuarioLaureanoSoul: readStoredUser('@cedapp:userLaureanoSoul'),
      usuarioHnsn: readStoredUser('@cedapp:userHnsn'),
      phatoAccess,
    });
  }, []);

  useEffect(() => {
    loadStorageData();
  }, [loadStorageData]);

  const storeSession = useCallback(
    ({ apiName, token, user }: PendingSession) => {
      const config = sessionConfigs[apiName];

      sessionStorage.setItem(AUTH_SCHEMA_KEY, AUTH_SCHEMA_VERSION);
      sessionStorage.setItem(config.tokenKey, token);
      sessionStorage.setItem(config.userKey, JSON.stringify(user));
      config.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setData((currentData) => ({
        ...currentData,
        [config.tokenField]: token,
        [config.userField]: user,
      }));
    },
    [],
  );

  const signInWith = useCallback(
    async (apiName: SessionApiName, credentials: SignInCredentials) => {
      const session = await requestApiSession(apiName, credentials);
      storeSession(session);
      return session.user;
    },
    [storeSession],
  );

  const signInPhato = useCallback(
    (credentials: SignInCredentials) => signInWith('phato', credentials),
    [signInWith],
  );

  const signInLaureano = useCallback(
    (credentials: SignInCredentials) => signInWith('laureano', credentials),
    [signInWith],
  );

  const signInLaureanoSoul = useCallback(
    (credentials: SignInCredentials) => signInWith('laureanoSoul', credentials),
    [signInWith],
  );

  const signInHnsn = useCallback(
    (credentials: SignInCredentials) => signInWith('hnsn', credentials),
    [signInWith],
  );

  const setPhatoAccess = useCallback(
    async (access: PhatoAccessContext) => {
      sessionStorage.setItem(AUTH_SCHEMA_KEY, AUTH_SCHEMA_VERSION);
      sessionStorage.setItem('@cedapp:phatoAccess', JSON.stringify(access));
      setData((currentData) => ({ ...currentData, phatoAccess: access }));
    },
    [],
  );

  const clearSession = useCallback(() => {
    clearStoredAuth();
    setData(emptyAuthState);
  }, []);

  // Substitui qualquer sessão anterior para não misturar tokens de usuários diferentes.
  const commitSessions = useCallback(
    async (
      sessions: PendingSession[],
      phatoAccess: PhatoAccessContext | null,
    ) => {
      clearSession();
      sessions.forEach(storeSession);
      if (phatoAccess) await setPhatoAccess(phatoAccess);
    },
    [clearSession, storeSession, setPhatoAccess],
  );

  const signOut = useCallback(() => {
    clearSession();
    window.location.href = '/login';
  }, [clearSession]);

  const setDataAuth = useCallback(async (state: AuthState) => {
    setData(state);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...data,
        signInPhato,
        signInLaureano,
        signInLaureanoSoul,
        signInHnsn,
        requestSession: requestApiSession,
        commitSessions,
        setPhatoAccess,
        clearSession,
        signOut,
        setData: setDataAuth,
        loadStorageData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
